import type { TurnstileExecutor } from './turnstile';
import { createTurnstileExecutor } from './turnstile';

export interface TurnstileGateConfig {
  /** Cloudflare site key; if falsy the gate always returns an empty token. */
  siteKey: string | null | undefined;
  /** Widget position — passed to the Turnstile executor for visual placement. */
  position?: string;
}

export interface TurnstileGate {
  /**
   * Return a Turnstile token for the next send request.
   * Always challenges on the first message (no conversation yet).
   * Returns an empty token for all subsequent messages — the backend signals
   * when verification is required via 403 turnstile_required, and the
   * controller retries with forceChallenge().
   *
   * @param hasConversation - true when a conversationId already exists.
   */
  getToken: (hasConversation: boolean) => Promise<string>;
  /**
   * Unconditionally show the Turnstile challenge and return a fresh token.
   * Used by the controller on 403 turnstile_required retries.
   */
  forceChallenge: () => Promise<string>;
}

/**
 * Factory for the Turnstile gate.
 * Call once per widget session; wire getToken into the controller's
 * getTurnstileToken option, and forceChallenge into the forceChallenge option.
 */
export function createTurnstileGate(config: TurnstileGateConfig): TurnstileGate {
  const { siteKey, position = 'bottom-right' } = config;

  let executorRef: TurnstileExecutor | null = null;

  async function getExecutor(): Promise<TurnstileExecutor> {
    if (!executorRef) {
      executorRef = await createTurnstileExecutor(siteKey!, position);
    }
    return executorRef;
  }

  async function getToken(hasConversation: boolean): Promise<string> {
    if (!siteKey) return '';
    if (hasConversation) return '';
    // First message (no conversation): always challenge.
    return (await getExecutor())();
  }

  async function forceChallenge(): Promise<string> {
    if (!siteKey) return '';
    return (await getExecutor())();
  }

  return { getToken, forceChallenge };
}
